import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { Table } from '../table';
import { SYSTEM_ROLES } from '@/utils/constants';
import { roleStore } from '@/store';
import RoleFormModal from './role-form-modal';
import { Role } from '@/types';
import AddButton from '@/components/button/add-button';
import { useTranslation } from 'react-i18next';

const RolesTable: React.FC = () => {
    const { t } = useTranslation();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const { roles } = roleStore();

    const openCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const openEditModal = (role: Role) => {
        // Prevent opening modal for system roles
        if (SYSTEM_ROLES.includes(role.name as typeof SYSTEM_ROLES[number])) {
            return;
        }
        setSelectedRole(role);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedRole(null);
    };

    const formattedRoles = useMemo(() => {
        if (!roles) return [];

        return roles.map((role: Role) => ({
            ...role,
            createdAtDisplay: role.createdAt ? dayjs(role.createdAt).format('MMM D, YYYY, h:mm A') : '',
            updatedAtDisplay: role.updatedAt ? dayjs(role.updatedAt).format('MMM D, YYYY, h:mm A') : '',
            isSystemRole: SYSTEM_ROLES.includes(role.name as typeof SYSTEM_ROLES[number]),
        }));
    }, [roles]);

    const columns = [
        { key: 'name', label: 'Role Name' },
        { key: 'description', label: 'Description' },
        { key: 'status', label: 'Status' },
        { key: 'createdAtDisplay', label: 'Created At' },
        { key: 'updatedAtDisplay', label: 'Updated At' },
        { key: 'actions', label: 'Actions' },
    ];

    const tableData = formattedRoles.map((role: Role & { isSystemRole: boolean }) => ({
        ...role,
        actions: (
            <button 
                className={`text-[#494C52] ${role.isSystemRole ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => openEditModal(role)}
                disabled={role.isSystemRole}
                title={role.isSystemRole ? "System roles cannot be edited" : ""}
            >
                <Icon icon="material-symbols:edit" fontSize={18} />
            </button>
        ),
    }));

    return (
        <div className="w-full">
            <Table
                paginate
                exportable
                data={tableData}
                columns={columns}
                title="Roles"
                exportData={tableData}
                additionalButton={
                    <AddButton 
                        handleClick={openCreateModal}
                        hoverText={t('settings.addNewRole')}
                    />
                }
            />
            
            {/* Create Role Modal */}
            <RoleFormModal 
                mode="create" 
                isOpen={isCreateModalOpen} 
                onClose={closeCreateModal} 
            />
            
            {/* Edit Role Modal */}
            {selectedRole && (
                <RoleFormModal 
                    mode="edit" 
                    role={selectedRole}
                    isOpen={isEditModalOpen} 
                    onClose={closeEditModal} 
                />
            )}
        </div>
    );
};

export default RolesTable;